import React, { useEffect, useState } from "react";
import { CompleteStatus } from "src/components/ui-components/ButtonOld/ButtonOld";
import { Session } from "src/utils/users/types";
import modalStyles from "src/components/ui-components/Modal/ModalContent.module.css";
import styles from "./InviteMemberDialog.module.css";
import cn from "classnames";
import { ApolloError, gql } from "@apollo/client";
import {
  InviteMemberDocument,
  InviteMemberMutation,
  InviteMemberMutationVariables,
} from "src/gql/codegen/graphql";
import { apolloCore } from "src/utils/apollo/apollo";
import { minDelay } from "src/components/page-components/Settings/Teams/util";
import { Identity } from "src/components/ui-components/Identity";
import { useInviteRoleOptions } from "./hooks/useInviteRoleOptions";
import { InputField } from "src/components/ui-components/Input/InputField";
import { SelectBox } from "src/components/ui-components/Input/SelectBox";
import { Button } from "src/components/ui-components/Button/Button";
import { useIsFeatureEnabled } from "src/hooks/useIsFeatureEnabled";
import { IconCopy } from "src/components/ui-components/Icons/IconCopy";
import CopyToClipboardButton from "src/components/CopyToClipboardButton/CopyToClipboardButton";
import { useTeamInviteLink } from "./hooks/useTeamInviteLink";
import { ButtonTextOnly } from "src/components/ui-components/ButtonTextOnly/ButtonTextOnly";
import { useRevokeAllInviteLinks } from "./hooks/useRevokeAllInviteLinks";
import { addToastNotification } from "shared/Toasts/toastNotificationStore";
import { useAnalytics } from "src/components/analytics/analytics";
import { isDefinedAndNotNull } from "src/utils/isDefinedAndNotNull";

export const InviteMemberDialogContent: React.FC<{
  onDismiss: () => void;
  team: Team;
  onInvite: () => Promise<unknown> | void;
  session: Session;
}> = (props) => {
  const { session, team } = props;
  const { captureEvent } = useAnalytics();
  const [userNameOrEmail, setUsernameOrEmail] = useState("");
  const [role, setRole] = useState("editor");
  const { role: userRole, options: roleOptions } = useInviteRoleOptions();
  const [inviteLink, createInviteLink] = useTeamInviteLink(team, session);
  const [revokeAllInviteLinks] = useRevokeAllInviteLinks();
  const isFeatureFlagEnabled = useIsFeatureEnabled(
    "invite-team-member-with-link"
  );

  useEffect(() => {
    if (inviteLink === undefined && isFeatureFlagEnabled) {
      createInviteLink();
    }
  }, [inviteLink, isFeatureFlagEnabled]);

  const onRevokeAllInviteLinks = () => {
    revokeAllInviteLinks(team.id, session)
      .then(() => {
        addToastNotification({
          level: "success",
          title:
            "All previous invite links were revoked. Please use the newest one.",
        });
        createInviteLink();
        captureEvent("Invite keys revoked", {
          team_id: team.id,
        });
      })
      .catch(() => {
        addToastNotification({
          level: "error",
          title: "Something went wrong. Please try again.",
        });
      });
  };

  const [{ isLoading, error, isSaved }, setRequestStatus] = useState<{
    isLoading: boolean;
    error?: Error;
    isSaved: boolean;
  }>({
    isLoading: false,
    error: undefined,
    isSaved: false,
  });

  const onSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    setRequestStatus({
      isLoading: true,
      error: undefined,
      isSaved: false,
    });

    minDelay(
      () =>
        callInviteTeamMember(
          props.team.id,
          userNameOrEmail,
          role,
          props.session
        ),
      500
    )
      .then(() => {
        setRequestStatus({
          isLoading: false,
          error: undefined,
          isSaved: true,
        });
        setTimeout(() => {
          setRequestStatus({
            isLoading: false,
            error: undefined,
            isSaved: false,
          });
          props.onInvite();
          setUsernameOrEmail("");
          setRole("editor");
        }, 1500);
      })
      .catch((err) => {
        setRequestStatus({
          isLoading: false,
          error: err,
          isSaved: false,
        });
      });
  };

  return (
    <div className={modalStyles.body}>
      <div className={modalStyles.header}>
        <div className={modalStyles.titleRow}>
          <h2>Invite team members</h2>

          <div className={styles.teamPositioner}>
            <Identity
              size="M"
              color="text-secondary"
              flip
              owner={{
                type: "team",
                id: props.team.id,
                handle: props.team.handle,
                profile_image_url: props.team.profile_image_url ?? "",
              }}
            />
          </div>
        </div>
      </div>
      <div className={cn(modalStyles.content, styles.content)}>
        <InputField
          size="L"
          style="contained"
          label="Invite by username or email"
          value={userNameOrEmail}
          placeholder="Username or email"
          onChange={(val) => setUsernameOrEmail(val.target.value)}
          hint="Enter the username or email of the person you want to invite."
          error={getErrorMessage(error)}
        />
        <SelectBox
          size="L"
          label="Role"
          name="Role"
          type="contained"
          onChange={(val) => setRole(val.target.value)}
          value={role}
          placeholder=""
          hint={
            <div className={styles.roles}>
              <dl>
                <dd>Viewer</dd>
                <dt>Browse team queries and dashboards.</dt>
                <dd>Editor</dd>
                <dt>Create, edit team queries and dashboards.</dt>
                {userRole === "admin" && (
                  <>
                    <dd>Admin</dd>
                    <dt>
                      Manage the team's profile, members and other settings.
                    </dt>
                  </>
                )}
              </dl>
            </div>
          }
        >
          {roleOptions.map((option) => (
            <option value={option.key} key={option.key}>
              {option.display}
            </option>
          ))}
        </SelectBox>

        {isFeatureFlagEnabled && isDefinedAndNotNull(inviteLink) && (
          <>
            <hr />
            <InputField
              size="L"
              style="contained"
              label="Allow joining team by url"
              readOnly
              value={inviteLink}
              placeholder=""
              onChange={() => {}}
              suffix={<CopyInviteLinkButton link={inviteLink} />}
              hint={
                <div>
                  Anyone with this link can automatically join your team.
                  Expires after 48h.
                  <div>
                    <ButtonTextOnly
                      size="M"
                      theme="danger"
                      onClick={onRevokeAllInviteLinks}
                    >
                      Revoke
                    </ButtonTextOnly>{" "}
                    all previous invite links.
                  </div>
                </div>
              }
            />
          </>
        )}
      </div>
      <div className={modalStyles.buttons}>
        <Button size="M" theme="primary" loading={isLoading} onClick={onSubmit}>
          Send Invite
        </Button>
        <Button onClick={props.onDismiss} size="M" theme="tertiary">
          Cancel
        </Button>
        {isSaved && <CompleteStatus text="Invite sent" />}
      </div>
    </div>
  );
};

function getErrorMessage(error?: Error) {
  if (error instanceof ApolloError) {
    switch (error.graphQLErrors[0]?.extensions?.key) {
      case "team_not_found":
        return "Team not found";
      case "invalid_input":
        return "Something went wrong, check your input and try again";
      case "invalid_email_or_username":
        return "Must either provide a valid email or username matching a user";
      case "not_authorized":
        return "You are not authorized to invite members to this team";
      case "max_invites_sent_per_hour_by_user":
        return "You have exceeded the maximum number of invites you can send per hour";
      case "max_invites_sent_per_day_to_user":
        return "This user has exceeded the maximum number of invites they can receive per day";
      default:
        return "Something went wrong";
    }
  }
  return;
}

function CopyInviteLinkButton({ link }: { link: string }) {
  return (
    <CopyToClipboardButton text={link} size="XS" theme="ghost">
      <IconCopy />
    </CopyToClipboardButton>
  );
}
export interface Team {
  id: number;
  handle: string;
  profile_image_url?: string | null | undefined;
}

const callInviteTeamMember = async (
  teamId: number,
  usernameOrEmail: string,
  role: string,
  session: Session
): Promise<string> => {
  const res = await apolloCore.mutate<
    InviteMemberMutation,
    InviteMemberMutationVariables
  >({
    mutation: InviteMemberDocument,
    variables: {
      team_id: teamId,
      role: role,
      usernameOrEmail: usernameOrEmail,
    },
    context: { session },
    fetchPolicy: "no-cache",
  });

  const memberId = res.data?.invite_member_v2?.id ?? "";

  if (!memberId) {
    throw new Error("Invite not sent");
  }

  return memberId;
};

gql`
  mutation InviteMember(
    $team_id: Int!
    $role: String!
    $usernameOrEmail: String!
  ) {
    invite_member_v2(
      team_id: $team_id
      role: $role
      usernameOrEmail: $usernameOrEmail
    ) {
      id
    }
  }
`;
